/**
 * Reads that die in transit.
 *
 * `makeAuthenticatedRequest` surfaced every `fetch` rejection to its caller on
 * the first try. For a WRITE that is the safe thing to do — replaying a PUT can
 * duplicate work, so those are recovered separately and deliberately by
 * `saveRecovery.js` — but for a READ it turns a momentary connection drop into
 * a broken screen the user can only fix by reloading.
 *
 * Sentry linkflare LINKFLARE-X / 140787295: four of its six events are
 * idempotent GETs (/links, /tags, /notifications, /subscriptions) that failed
 * with "Failed to fetch" while the browser stayed online. Server-side spans for
 * the most recent one show the backend serving other linkflare endpoints
 * normally in the same window, with no span for the failed path at all — the
 * request never arrived, so sending it again is the whole fix.
 *
 * Dependency-free and side-effect-free at import time so the policy can be
 * unit-tested without a network or a Vue app (same shape as lazyComponent.js).
 */

// Methods that are safe to send twice: a retry must not be able to change
// server state. GET/HEAD cannot; everything else can.
export const IDEMPOTENT_METHODS = new Set(['GET', 'HEAD'])

export const TRANSPORT_RETRIES = 2

// The first retry is immediate — a connection that died in transit almost
// always succeeds at once on a fresh one, and every millisecond spent here is a
// millisecond the user spends watching a spinner. The second waits out a flap.
export const TRANSPORT_RETRY_DELAYS_MS = [0, 600]

export function transportRetryDelayMs(attempt) {
  const delays = TRANSPORT_RETRY_DELAYS_MS
  return delays[Math.max(0, attempt - 1)] ?? delays[delays.length - 1]
}

/**
 * The status `api.js` encoded into a thrown error's message, or null when the
 * error carries no status we can trust.
 *
 * Read ONLY from the `HTTP error! status: <n>` prefix `api.js` builds, never
 * from anywhere else in the string: the rest of the message is the backend's
 * own error body, so a payload quoting `{"status":500}` must not be able to
 * pass itself off as a 500. Anchored for that reason.
 *
 * Lives here rather than in each caller because the message shape is
 * load-bearing (`api.js` says so where it builds it) and three places were
 * parsing it independently — which is how they came to disagree about what a
 * 5xx means.
 */
export function httpStatusFromError(error) {
  const match = /^HTTP error! status: (\d+)/.exec(error?.message || '')
  return match ? Number(match[1]) : null
}

/**
 * A transport failure is `fetch` itself rejecting: DNS, TLS, connection reset,
 * a blocked CORS preflight. Browsers report every one of them as a bare
 * TypeError. Matched by name rather than `instanceof` so it still holds across
 * realm boundaries (jsdom, iframes), where `instanceof TypeError` silently fails.
 */
export function isTransportFailure(error) {
  return error?.name === 'TypeError'
}

/**
 * The retry decision, extracted so it is testable without issuing a request.
 *
 * @param {object} params
 * @param {string} params.method        — HTTP method of the request
 * @param {Error}  params.error         — what the attempt threw
 * @param {number} params.attempt       — retries already spent (0 on first failure)
 * @param {boolean} params.reachedServer — true once a response object came back
 * @param {number} [params.retries]     — retry budget
 * @param {boolean} [params.isOnline]   — navigator.onLine at the time of failure
 */
export function shouldRetryRequest({
  method,
  error,
  attempt,
  reachedServer,
  retries = TRANSPORT_RETRIES,
  isOnline = true
}) {
  if (attempt >= retries) return false
  if (!IDEMPOTENT_METHODS.has(String(method || 'GET').toUpperCase())) return false

  // A response came back. Whatever failed after it — an HTTP status, JSON that
  // will not parse — is deterministic and will fail again identically.
  if (reachedServer) return false

  // A timeout is the opposite case: the server may well still be working on it,
  // so a retry stacks a second unit of work onto a backend that is already slow.
  if (error?.name === 'AbortError' || error?.name === 'TimeoutError') return false

  // Offline is the user's network, not a request lost in transit. The PWA is
  // built to fail here and `reportApiFailure` already stays quiet about it.
  if (!isOnline) return false

  // Everything is failing — stop adding load until something gets through.
  if (transportRetriesSuspended()) return false

  return isTransportFailure(error)
}

// Retrying one lost read is repair; retrying every read during an outage is a
// stampede against a backend that is already struggling. After this many reads
// in a row die in transit the condition is clearly systemic rather than a blip,
// so retries switch off until something succeeds again.
export const TRANSPORT_FAILURE_STREAK_LIMIT = 3

let consecutiveTransportFailures = 0

/**
 * Record how a request ended. Cheap module-level state on purpose: the signal
 * is inherently cross-request, and `api.js` already keeps its reporting dedupe
 * the same way.
 */
export function noteTransportOutcome(succeeded) {
  consecutiveTransportFailures = succeeded ? 0 : consecutiveTransportFailures + 1
}

export function transportRetriesSuspended() {
  return consecutiveTransportFailures >= TRANSPORT_FAILURE_STREAK_LIMIT
}

/** Test seam — also resets naturally in production on the first success. */
export function resetTransportFailureStreak() {
  consecutiveTransportFailures = 0
}

/** Awaitable pause. Zero resolves without ever reaching the macrotask queue. */
export function pause(ms, schedule = (fn, delay) => setTimeout(fn, delay)) {
  if (!ms) return Promise.resolve()
  return new Promise((resolve) => schedule(resolve, ms))
}
